import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import api from "../../api/axios";
import toast from "react-hot-toast";
import { Plus, Trash2, Edit, GripVertical } from "lucide-react";

function StrictModeDroppable(props) {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));
    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);
  if (!enabled) return null;
  return <Droppable {...props} />;
}

export default function ServiceListPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchServices = async () => {
    try {
      const response = await api.get("/service");
      const sorted = response.data
        .map((s, i) => ({
          ...s,
          imageUrl: s.imagePath
            ? `${process.env.REACT_APP_UPLOAD_URL}/${s.imagePath}`
            : null,
          sortOrder: s.sortOrder || i + 1,
        }))
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      setServices(sorted);
    } catch (err) {
      toast.error("Hizmetler yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const handleDelete = (id) => {
    if (!window.confirm("Bu hizmeti silmek istediğinize emin misiniz?")) return;
    api
      .delete(`/service/${id}`)
      .then(() => {
        toast.success("Hizmet silindi");
        fetchServices();
      })
      .catch(() => toast.error("Silme işlemi başarısız"));
  };

  const onDragEnd = async (result) => {
    if (!result.destination || result.destination.index === result.source.index)
      return;

    const items = Array.from(services);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    setServices(items);

    try {
      await api.post(
        "/service/reorder",
        items.map((s, i) => ({ Id: s.serviceId, OrderNumber: i + 1 })),
      );
      toast.success("Sıralama güncellendi");
    } catch {
      toast.error("Sıralama güncellenemedi");
      fetchServices();
    }
  };

  if (loading)
    return (
      <div className="page-loading">
        <div className="loading-spinner" />
        <p>Yükleniyor...</p>
      </div>
    );

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h2>Hizmetler</h2>
          <p className="page-subtitle">{services.length} hizmet listeleniyor</p>
        </div>
        <Link to="/add-service" className="btn btn-primary">
          <Plus size={18} /> Yeni Hizmet
        </Link>
      </div>

      <div className="content-card no-padding">
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 50 }}></th>
                  <th>Resim</th>
                  <th>Başlık (TR)</th>
                  <th>Başlık (EN)</th>
                  <th>Durum</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <StrictModeDroppable droppableId="services">
                {(provided) => (
                  <tbody ref={provided.innerRef} {...provided.droppableProps}>
                    {services.map((service, index) => (
                      <Draggable
                        key={String(service.serviceId)}
                        draggableId={String(service.serviceId)}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <tr
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={snapshot.isDragging ? "dragging" : ""}
                          >
                            <td
                              {...provided.dragHandleProps}
                              className="drag-handle"
                            >
                              <GripVertical size={18} />
                            </td>
                            <td>
                              {service.imageUrl ? (
                                <img
                                  src={service.imageUrl}
                                  alt=""
                                  className="table-thumb"
                                />
                              ) : (
                                <div className="table-thumb-placeholder" />
                              )}
                            </td>
                            <td className="fw-medium">{service.nameTr}</td>
                            <td>{service.nameEn}</td>
                            <td>
                              <span
                                className={`status-badge ${service.isActive ? "active" : "inactive"}`}
                              >
                                {service.isActive ? "Aktif" : "Pasif"}
                              </span>
                            </td>
                            <td>
                              <div className="action-buttons">
                                <Link
                                  to={`/edit-service/${service.serviceId}`}
                                  className="btn-icon edit"
                                  title="Düzenle"
                                >
                                  <Edit size={16} />
                                </Link>
                                <button
                                  onClick={() =>
                                    handleDelete(service.serviceId)
                                  }
                                  className="btn-icon delete"
                                  title="Sil"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </tbody>
                )}
              </StrictModeDroppable>
            </table>
          </div>
        </DragDropContext>

        {services.length === 0 && (
          <div className="empty-state">
            <p>Henüz hizmet eklenmemiş</p>
            <Link to="/add-service" className="btn btn-primary">
              <Plus size={18} /> İlk Hizmeti Ekle
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
